import { useAxios } from '@/hooks/useAxios';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { useOsintContext } from '@/state/OsintContext';
import type { Asset, Attribute, CloudResource, Port, Webpage } from '@/types';
import { useQuery } from '@/utils/api';

type Response = {
  node: {
    relationships: {
      result: {
        hasvulnerabilities: {
          attachmentPath: string;
        }[];
      };
    }[];
    result: {
      assets?: Asset[];
      repositories?: Asset[];
      awsresources?: CloudResource[];
      azureresources?: CloudResource[];
      gcpresources?: CloudResource[];
      ports?: Port[];
      webpages: Webpage[];
      attributes: Attribute[];
    };
  };
};

export type EvidenceFile = {
  attachmentPath: string;
  webpage: Webpage;
  asset?: Asset;
  port?: Port;
};

export function useEvidenceFiles(riskKey: string) {
  const { isOsint } = useOsintContext();
  const axios = useAxios();

  return useQuery({
    queryKey: [
      ...getQueryKey.getEvidenceFiles(riskKey),
      ...(isOsint ? [isOsint] : []),
    ],
    queryFn: async () => {
      const { data } = await axios({
        method: 'POST',
        url: '/my',
        params: {
          tree: true,
          ...(isOsint ? { osint: 'true' } : {}),
        },
        data: {
          node: {
            relationships: [
              {
                label: 'HAS_VULNERABILITY',
                target: {
                  labels: ['Risk'],
                  filters: [
                    {
                      field: 'key',
                      operator: '=',
                      value: riskKey,
                    },
                  ],
                },
              },
            ],
          },
        },
      });

      const evidenceFiles = data
        .map((entry: Response) => {
          const attachmentPath =
            entry?.node?.relationships?.[0]?.result?.hasvulnerabilities?.[0]
              ?.attachmentPath;
          const asset =
            entry?.node?.result?.assets?.[0] ||
            entry?.node?.result?.repositories?.[0] ||
            entry?.node?.result?.awsresources?.[0] ||
            entry?.node?.result?.azureresources?.[0] ||
            entry?.node?.result?.gcpresources?.[0];
          const port = entry?.node?.result?.ports?.[0];
          const webpage = entry?.node?.result?.webpages?.[0];
          const attributes = entry?.node?.result?.attributes?.[0];

          if (!attachmentPath) return null;

          return {
            attachmentPath,
            webpage,
            asset,
            port,
            attributes,
          };
        })
        .filter(Boolean);

      return evidenceFiles as EvidenceFile[];
    },
    defaultErrorMessage: 'Failed to fetch evidence files',
    enabled: !!riskKey,
  });
}
